import { Key, Eye, EyeOff, Check, Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";

export default function ApiKeysCard({
  darkMode,
  apiKeysConfig,
  useCustomKey,
  setUseCustomKey,
  openaiKey,
  setOpenaiKey,
  showKey,
  setShowKey,
  savingKey,
  saveApiKeys,
  deleteApiKey,
}) {
  return (
    <div
      className={`rounded-xl overflow-hidden lg:col-span-2 ${
        darkMode ? "bg-[#161b22] border border-[#30363d]" : "bg-white border border-gray-100 shadow-sm"
      }`}
      data-testid="api-keys-settings"
    >
      <div className={`px-5 py-4 border-b ${darkMode ? "border-[#21262d]" : "border-gray-100"}`}>
        <h2 className={`font-semibold flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
          <Key className="w-5 h-5 text-[#DC143C]" />
          API Keys
        </h2>
        <p className={`text-sm mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
          Configure API keys for AI services (GPT-5.2 & Sora 2)
        </p>
      </div>
      <div className="p-5 space-y-5">
        {/* Current Status */}
        <div className={`p-4 rounded-lg ${darkMode ? "bg-[#21262d]" : "bg-gray-50"}`}>
          <div className="flex items-center justify-between mb-4">
            <span className={`font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
              Current Configuration
            </span>
            <Badge
              className={
                apiKeysConfig?.use_custom_key
                  ? "bg-[#DC143C]/10 text-[#DC143C] border-0"
                  : "bg-emerald-100 text-emerald-700 border-0"
              }
            >
              {apiKeysConfig?.use_custom_key ? "Custom Key" : "Emergent Key"}
            </Badge>
          </div>
          {apiKeysConfig?.use_custom_key && apiKeysConfig?.has_openai_key && (
            <div className="flex items-center justify-between">
              <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                OpenAI Key: {apiKeysConfig.openai_key_preview}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={deleteApiKey}
                className={`text-red-500 hover:text-red-600 ${
                  darkMode ? "border-[#30363d] hover:bg-red-900/20" : "hover:bg-red-50"
                }`}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Remove
              </Button>
            </div>
          )}
          {!apiKeysConfig?.use_custom_key && (
            <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Using Emergent Universal Key. Add balance at Profile → Universal Key → Add Balance
            </p>
          )}
        </div>

        {/* Add Custom Key */}
        <div className={`p-4 rounded-lg ${darkMode ? "bg-[#21262d]" : "bg-gray-50"}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <Label className={`font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                Use Custom OpenAI Key
              </Label>
              <p className={`text-sm mt-0.5 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Use your own OpenAI API key instead of Emergent key
              </p>
            </div>
            <Switch
              checked={useCustomKey}
              onCheckedChange={setUseCustomKey}
              className="data-[state=checked]:bg-[#DC143C]"
            />
          </div>

          {useCustomKey && (
            <div
              className="space-y-4 mt-4 pt-4 border-t border-dashed"
              style={{ borderColor: darkMode ? "#30363d" : "#e5e7eb" }}
            >
              <div>
                <Label className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                  OpenAI API Key
                </Label>
                <div className="flex gap-2 mt-2">
                  <div className="relative flex-1">
                    <Input
                      type={showKey ? "text" : "password"}
                      value={openaiKey}
                      onChange={(e) => setOpenaiKey(e.target.value)}
                      placeholder="sk-..."
                      className={`pr-10 ${darkMode ? "bg-[#161b22] border-[#30363d] text-white" : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                        darkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                      }`}
                    >
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <p className={`text-xs mt-2 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                  Get your API key from{" "}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#DC143C] hover:underline"
                  >
                    platform.openai.com/api-keys
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={saveApiKeys}
          disabled={savingKey || (useCustomKey && !openaiKey && !apiKeysConfig?.has_openai_key)}
          className="w-full bg-[#DC143C] hover:bg-[#b91031] text-white"
        >
          {savingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Save API Configuration
        </Button>
      </div>
    </div>
  );
}
